// frontend/shared/src/errors.ts —— TD-839；键名逐字取 06 三册错误码表
import { ApiError, NETWORK_CODE } from './api/client';

export type ErrApp = 'user' | 'agent' | 'admin';
export type Dict = Readonly<Record<string, unknown>>;

export const USER_ERRORS: Readonly<Record<number, string>> = {
  1001: 'errCredential', 1002: 'errLocked', 1003: 'errCode', 1004: 'need2faTip', 1005: 'errEmailUsed', 1006: 'err2fa',
  1007: 'errUnbindOnly', 1008: 'errTgTaken', 1009: 'errNeedMerge', 1010: 'errPhoneClosed', 1015: 'errEmailLoginRequired',
  1101: 'errBalance', 1102: 'errWdAmount', 1104: 'errWdAddrFormat', 1105: 'errFeeOwed',
  1201: 'errProductOff', 1202: 'errMaxCards', 1203: 'kycGuide', 1204: 'errIntAmount', 1205: 'errCardState', 1206: 'errMinAmount',
  1207: 'errCardDeduct', 1208: 'errCapability', 1209: 'errCardLimit', 1210: 'errKycState', 1212: 'errCardInflight',
  1302: 'errCommNone', 1401: 'errUpstream', 1403: 'errUpstreamDown', 1503: 'errSwitchOff',
};

export const AGENT_ERRORS: Readonly<Record<number, string>> = {
  1001: 'err1001', 1002: 'err1002', 1005: 'err1005', 1301: 'errRate', 1302: 'errAmount', 1303: 'errRateCap', 1304: 'errSubDelBlocked',
  1305: 'noSubTip', 1306: 'err1306', 1307: 'errWalletUnbound', 1308: 'errMinWd', 1310: 'errPriceBelowParent', 1501: 'errNoPerm', 1503: 'err1503', 1504: 'errExportLimit',
};

export const ADMIN_ERRORS: Readonly<Record<number, string>> = {
  1001: 'err1001', 1002: 'err1002', 1004: 'err1004', 1005: 'err1005', 1006: 'err1006', 1008: 'err1008', 1105: 'err1105',
  1205: 'err1205', 1207: 'err1207', 1208: 'err1208', 1209: 'err1209', 1211: 'err1211', 1212: 'err1212',
  1301: 'err1301', 1303: 'err1303', 1304: 'err1304', 1305: 'err1305', 1306: 'err1306', 1310: 'errPriceBelowParent', 1401: 'err1401', 1403: 'err1403',
  1501: 'toastNoPerm', 1503: 'err1503', 1504: 'errExportLimit',
};

const TABLES: Readonly<Record<ErrApp, Readonly<Record<number, string>>>> = { user: USER_ERRORS, agent: AGENT_ERRORS, admin: ADMIN_ERRORS };
const NETWORK_KEY: Readonly<Record<ErrApp, string>> = { user: 'errNetwork', agent: 'toastNetwork', admin: 'toastNetwork' };
const BELOW_KEY: Readonly<Record<ErrApp, string>> = { user: '', agent: 'errRateBelowSub', admin: 'err1301Below' };

export type ErrContext = 'register' | undefined;

export function errorKey(app: ErrApp, err: ApiError, ctx?: ErrContext): string | null {
  if (err.code === NETWORK_CODE || err.code === 5000) return NETWORK_KEY[app];
  if (app === 'user' && err.code === 1503 && err.data.reason === 'withdraw_trc20_only') return 'errWdTrcOnly';
  // 批 5（P-15 / 用户决定 1）：付款池不可用时拒绝提现 / USDT 提佣申请（不冻结），用户与代理共用文案
  if ((app === 'user' || app === 'agent') && err.code === 1503 && err.data.reason === 'payout_channel_maintenance') return 'errWdChannelMaint';
  if (app === 'user' && err.code === 1012 && err.data.reason === 'recipient_zero_usdt') return 'errWdRecipientZero';
  if (app === 'user' && err.code === 1012 && err.data.reason === 'recipient_balance_unavailable') return 'errWdBalanceCheck';
  // 0050：单用户当日开卡数已达 maxOpenPerDay（data.max = 上限张数）
  if (app === 'user' && err.code === 1012 && err.data.reason === 'daily_open_limit') return 'errDailyOpenLimit';
  // P-14 / P-23：收款地址为平台内部地址（用户提币与代理 USDT 提佣共用 1104 reason=platform_address）
  if ((app === 'user' || app === 'agent') && err.code === 1104 && err.data.reason === 'platform_address') return 'errWdPlatformAddr';
  if (app === 'user' && err.code === 1503 && ctx === 'register') return 'regClosed';
  if (app === 'user' && err.code === 1212 && err.data.awaiting_manual === true) return 'errCardInflightManual';
  // 销卡前预检：卡上有未完结授权（data.pending_count 笔），用户端与后台共用键名
  if ((app === 'user' || app === 'admin') && err.code === 1205 && err.data.reason === 'pending_authorization') return 'errCardPendingAuth';
  if (err.code === 1301 && err.data.reason === 'below_child') return BELOW_KEY[app] || null;
  // 多级代理（2026-10-18 D3）：删除代理时后代数超过上限（5000）→ 1012 reason=subtree_too_large
  if (app === 'admin' && err.code === 1012 && err.data.reason === 'subtree_too_large') return 'errSubtreeTooLarge';
  return TABLES[app][err.code] ?? null;
}

function str(d: Dict, k: string): string | null {
  const v = d[k];
  return typeof v === 'string' ? v : null;
}

export function interpolate(tpl: string, vars: Readonly<Record<string, string | number | undefined>>): string {
  return tpl.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = vars[k];
    return v === undefined ? m : String(v);
  });
}

// vars：页面上下文值（min / fee_owed / max_cards / min_amount 等不在 data 中的参数）
export function errorText(app: ErrApp, err: ApiError, dict: Dict, vars: Readonly<Record<string, string | number | undefined>> = {}, ctx?: ErrContext): string {
  if (app === 'user' && err.code === 1207 && err.data.reason === 'spent_unverified') {
    return str(dict, 'spentPendingHint') || err.message;
  }
  // 方案 A：上游无消费快照时按交易推导，卡刚有交易处于冷静期（data.retry_after 秒，向上取整为分钟）。
  if (app === 'user' && err.code === 1207 && err.data.reason === 'spent_cooling') {
    const tpl = str(dict, 'spentCoolingHint');
    if (!tpl) return err.message;
    return interpolate(tpl, { minutes: Math.max(1, Math.ceil((err.retryAfter ?? 600) / 60)) });
  }
  if (err.code === 1301 && !err.data.reason && app !== 'user') {
    const a = str(dict, TABLES[app][1301] ?? '');
    const b = str(dict, BELOW_KEY[app]);
    if (a && b) return `${a}；${b}`;
  }
  if (err.code === 4220) {
    const tpl = str(dict, 'err4220');
    return tpl && err.data.field ? interpolate(tpl, { field: err.data.field }) : err.message;
  }
  const key = errorKey(app, err, ctx);
  const tpl = key ? str(dict, key) : null;
  if (!tpl) return err.message || str(dict, NETWORK_KEY[app]) || '';
  const all = {
    ...vars,
    n: err.data.pending_count ?? err.retryAfter ?? vars.n,
    max: err.data.max ?? vars.max,
    message: err.message,
    inflight_biz_no: err.data.inflight_biz_no ?? vars.inflight_biz_no,
    parent_eff: (err.code === 1310 ? firstParentEff(err.data.items) : undefined) ?? vars.parent_eff,
  };
  const text = interpolate(tpl, all);
  if (app === 'admin' && (err.code === 1207 || err.code === 1211) && err.data.reason) return `${text}（${err.data.reason}）`;
  return text;
}

// 1310 PriceBelowParent：取 data.items 首项的上级生效价（去尾零）插值 {parent_eff}；逐行标红由 shared/prices.ts 处理
function firstParentEff(items: readonly unknown[] | undefined): string | undefined {
  const it: unknown = items?.[0];
  if (typeof it !== 'object' || it === null) return undefined;
  const v: unknown = Reflect.get(it, 'parent_eff');
  if (typeof v !== 'string') return undefined;
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v;
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
